import {
  isClimateMonitoringSignalResult,
  isFinanceMonitoringSignalResult,
  isOwnershipMonitoringSignalResult,
  isRegulationMonitoringSignalResult,
  isReputationMonitoringSignalResult,
} from 'constants/interfaces/IMonitoringSignalResult';

import type {
  IClimateFinding,
  IClimateMonitoredEntity,
  IClimateMonitoringSignalResult,
  IClimateResultsSummary,
  IFinanceFinding,
  IFinanceMonitoredEntity,
  IFinanceMonitoringSignalResult,
  IFinanceProfileYear,
  IFinanceResultsSummary,
  IMonitoringArticle,
  IMonitoringResultsSummary,
  IOwnershipFinding,
  IOwnershipMonitoredEntity,
  IOwnershipMonitoringSignalResult,
  IOwnershipResultsSummary,
  IRegulationMonitoringSignalResult,
  IRegulationResultsSummary,
  IRegulationSignal,
  TMonitoringAnySignalResult,
  TMonitoringImpactLevel,
  TMonitoringRiskSeverity,
} from 'constants/interfaces/IMonitoringSignalResult';
import type IMonitoringSignalResult from 'constants/interfaces/IMonitoringSignalResult';

const RISK_SEVERITY_ORDER: Record<TMonitoringRiskSeverity, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

const IMPACT_LEVEL_ORDER: Record<TMonitoringImpactLevel, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
};

const FALLBACK_ARTICLE_TEMPLATES: Omit<IMonitoringArticle, 'title' | 'keywords_found'>[] = [
  {
    date: '2026-07-14',
    source: 'Industry weekly',
    url: 'https://example.com/articles/insolvency-trade-press',
    summary: 'Trade press reports heightened financial distress signals for the supplier.',
    sentiment: 'negative',
    risk_severity: 'high',
    confidence: 0.91,
    excerpt: 'Creditors are reportedly convening as liquidity pressures mount across the specialty chemicals segment.',
  },
  {
    date: '2026-07-12',
    source: 'ECHA bulletin',
    url: 'https://example.com/articles/reach-review',
    summary: 'Regulatory bulletin references the supplier in an EU chemical safety review.',
    sentiment: 'negative',
    risk_severity: 'medium',
    confidence: 0.78,
    excerpt: 'The review cites upstream manufacturers named in a broader REACH compliance audit.',
  },
  {
    date: '2026-07-10',
    source: 'LinkedIn',
    summary: 'Social channels discuss labour practices linked to the supplier network.',
    sentiment: 'neutral',
    risk_severity: 'low',
    confidence: 0.62,
    excerpt: 'Employee advocacy posts mention working-hour policies at contract manufacturers.',
  },
];

const FALLBACK_REGULATION_TEMPLATES: Omit<
IRegulationSignal,
'id' | 'product_name' | 'product_unit_id'
>[] = [
  {
    related_materials: ['Adhesive Materials'],
    factory_location: {
      label: 'ATTAIMAIRELAND / Dublin, Ireland',
      country: 'Ireland',
      region: 'Leinster',
    },
    regulation_name: 'Framework Regulation (EC) No. 1935/2004',
    regulatory_body: 'European Commission',
    jurisdiction: 'European Union',
    source_type: 'official',
    source_links: [
      {
        label: 'European Commission - Food Contact Materials',
        url: 'https://food.ec.europa.eu/food-safety/chemical-safety/food-contact-materials_en',
      },
    ],
    impact_level: 'high',
    impact_summary:
      'Compliance evidence may be required under the EU food-contact framework for this product.',
    approval_state: 'in-force',
    enacted_date: '2004-10-27',
    effective_date: '2005-12-03',
    expected_enforcement_date: null,
    confidence: 0.86,
    recommended_action: 'Confirm end use and collect declaration-of-compliance evidence.',
  },
  {
    related_materials: ['Polyethylene Film'],
    factory_location: {
      label: 'ATTAIMAIRELAND / Dublin, Ireland',
      country: 'Ireland',
      region: 'Leinster',
    },
    regulation_name: 'Possible national consultation on fluorinated barrier coatings',
    regulatory_body: 'Unknown / not yet formalized',
    jurisdiction: 'France',
    source_type: 'media',
    source_links: [
      {
        label: 'Industry article',
        url: 'https://example.com/regulation-watch/fr-barrier-coatings',
      },
    ],
    impact_level: 'low',
    impact_summary:
      'Trade coverage suggests a possible consultation, but no formal regulator text has been confirmed.',
    approval_state: 'rumor',
    enacted_date: null,
    effective_date: null,
    expected_enforcement_date: null,
    confidence: 0.34,
    recommended_action: 'Keep on watchlist only; do not trigger operational response yet.',
  },
];

export const isHighRiskArticle = (article: IMonitoringArticle): boolean => (
  article.risk_severity === 'high' || article.risk_severity === 'critical'
);

export const isHighImpactRegulation = (regulation: IRegulationSignal): boolean => (
  regulation.impact_level === 'high' || regulation.impact_level === 'critical'
);

export const countHighRiskArticles = (articles: IMonitoringArticle[]): number => (
  articles.filter(isHighRiskArticle).length
);

export const buildKeywordsFrequency = (
  articles: IMonitoringArticle[],
): Record<string, number> => (
  articles.reduce<Record<string, number>>((frequencyMap, article) => (
    (article.keywords_found || []).reduce((nextFrequencyMap, keyword) => {
      const normalizedKeyword = keyword.trim().toLowerCase();

      if (!normalizedKeyword) {
        return nextFrequencyMap;
      }

      return {
        ...nextFrequencyMap,
        [normalizedKeyword]: (nextFrequencyMap[normalizedKeyword] || 0) + 1,
      };
    }, frequencyMap)
  ), {})
);

export const buildResultsSummary = (articles: IMonitoringArticle[]): IMonitoringResultsSummary => ({
  total_articles: articles.length,
  high_risk_count: countHighRiskArticles(articles),
  keywords_frequency: buildKeywordsFrequency(articles),
});

export const buildRegulationResultsSummary = (
  regulations: IRegulationSignal[],
): IRegulationResultsSummary => ({
  total_results: regulations.length,
  high_impact_count: regulations.filter((regulation) => regulation.impact_level === 'high').length,
  critical_impact_count: regulations.filter(
    (regulation) => regulation.impact_level === 'critical',
  ).length,
});

export const sortMonitoringArticles = (articles: IMonitoringArticle[]): IMonitoringArticle[] => (
  [...articles].sort((leftArticle, rightArticle) => {
    const riskDifference = RISK_SEVERITY_ORDER[leftArticle.risk_severity]
      - RISK_SEVERITY_ORDER[rightArticle.risk_severity];

    if (riskDifference !== 0) {
      return riskDifference;
    }

    return rightArticle.date.localeCompare(leftArticle.date);
  })
);

const getRegulationSortDate = (regulation: IRegulationSignal): string => (
  regulation.effective_date
  || regulation.expected_enforcement_date
  || regulation.enacted_date
  || ''
);

export const sortRegulationSignals = (
  regulations: IRegulationSignal[],
): IRegulationSignal[] => (
  [...regulations].sort((leftRegulation, rightRegulation) => {
    const impactDifference = IMPACT_LEVEL_ORDER[leftRegulation.impact_level]
      - IMPACT_LEVEL_ORDER[rightRegulation.impact_level];

    if (impactDifference !== 0) {
      return impactDifference;
    }

    const leftDate = getRegulationSortDate(leftRegulation);
    const rightDate = getRegulationSortDate(rightRegulation);

    if (!leftDate && !rightDate) {
      return 0;
    }

    if (!leftDate) {
      return 1;
    }

    if (!rightDate) {
      return -1;
    }

    return leftDate.localeCompare(rightDate);
  })
);

export const getTopKeywords = (
  keywordsFrequency: Record<string, number>,
  limit = 6,
): { keyword: string, count: number }[] => (
  Object.entries(keywordsFrequency)
    .map(([keyword, count]) => ({ keyword, count }))
    .sort((leftEntry, rightEntry) => rightEntry.count - leftEntry.count)
    .slice(0, limit)
);

export const isHighSeverityFinanceFinding = (finding: IFinanceFinding): boolean => (
  finding.severity === 'high' || finding.severity === 'critical'
);

export const isHighSeverityOwnershipFinding = (finding: IOwnershipFinding): boolean => (
  finding.severity === 'high' || finding.severity === 'critical'
);

export const isHighSeverityClimateFinding = (finding: IClimateFinding): boolean => (
  finding.severity === 'high' || finding.severity === 'critical'
);

/** Contagion score bands mirror the entity-card badges: >=70 high, >=40 mid, else low. */
export const getOwnershipContagionLevel = (score: number): 'low' | 'mid' | 'high' => {
  if (score >= 70) {
    return 'high';
  }

  if (score >= 40) {
    return 'mid';
  }

  return 'low';
};

/** Exposure score bands: >=70 high, >=40 mid, else low (higher = worse). */
export const getClimateExposureLevel = (score: number): 'low' | 'mid' | 'high' => {
  if (score >= 70) {
    return 'high';
  }

  if (score >= 40) {
    return 'mid';
  }

  return 'low';
};

/** "Active" contagion = mid or high band (score >= 40), used for the summary count. */
export const isActiveOwnershipContagion = (entity: IOwnershipMonitoredEntity): boolean => (
  entity.contagion_score >= 40
);

export const isActiveClimateHazard = (entity: IClimateMonitoredEntity): boolean => (
  entity.exposure_score >= 40
);

export const isActiveFinanceBankruptcy = (entity: IFinanceMonitoredEntity): boolean => (
  Boolean(entity.bankruptcy_status?.is_active)
);

export const sortFinanceFindings = (findings: IFinanceFinding[]): IFinanceFinding[] => (
  [...findings].sort((leftFinding, rightFinding) => {
    const severityDifference = RISK_SEVERITY_ORDER[leftFinding.severity]
      - RISK_SEVERITY_ORDER[rightFinding.severity];

    if (severityDifference !== 0) {
      return severityDifference;
    }

    return rightFinding.observed_at.localeCompare(leftFinding.observed_at);
  })
);

export const sortOwnershipFindings = (findings: IOwnershipFinding[]): IOwnershipFinding[] => (
  [...findings].sort((leftFinding, rightFinding) => {
    const severityDifference = RISK_SEVERITY_ORDER[leftFinding.severity]
      - RISK_SEVERITY_ORDER[rightFinding.severity];

    if (severityDifference !== 0) {
      return severityDifference;
    }

    return rightFinding.observed_at.localeCompare(leftFinding.observed_at);
  })
);

export const sortClimateFindings = (findings: IClimateFinding[]): IClimateFinding[] => (
  [...findings].sort((leftFinding, rightFinding) => {
    const severityDifference = RISK_SEVERITY_ORDER[leftFinding.severity]
      - RISK_SEVERITY_ORDER[rightFinding.severity];

    if (severityDifference !== 0) {
      return severityDifference;
    }

    return rightFinding.observed_at.localeCompare(leftFinding.observed_at);
  })
);

export const sortFinanceProfileYears = (years: IFinanceProfileYear[]): IFinanceProfileYear[] => (
  [...years].sort((leftYear, rightYear) => leftYear.year_index - rightYear.year_index)
);

export const formatFinanceLiquidityRetention = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'n/a';
  }

  return `${value.toFixed(2)}x`;
};

export const formatFinanceMoney = (
  value: number | null | undefined,
  currency = 'USD',
): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'n/a';
  }

  const absoluteValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absoluteValue >= 1_000_000_000) {
    return `${sign}${(absoluteValue / 1_000_000_000).toFixed(1)}B ${currency}`;
  }

  if (absoluteValue >= 1_000_000) {
    return `${sign}${(absoluteValue / 1_000_000).toFixed(0)}M ${currency}`;
  }

  return `${sign}${absoluteValue.toLocaleString()} ${currency}`;
};

export const buildFinanceResultsSummary = (
  findings: IFinanceFinding[],
  entities: IFinanceMonitoredEntity[],
): IFinanceResultsSummary => {
  const themesFrequency = findings.reduce<Record<string, number>>((frequencyMap, finding) => {
    const theme = finding.theme.trim().toLowerCase();

    if (!theme) {
      return frequencyMap;
    }

    return {
      ...frequencyMap,
      [theme]: (frequencyMap[theme] || 0) + 1,
    };
  }, {});

  const highFindingCount = findings.filter(isHighSeverityFinanceFinding).length;
  const protectionEntities = entities.filter(isActiveFinanceBankruptcy);
  const uncoveredProtectionCount = protectionEntities.filter((entity) => (
    !findings.some((finding) => (
      finding.supplier_unit_id === entity.unitId
      && isHighSeverityFinanceFinding(finding)
    ))
  )).length;

  return {
    total_findings: findings.length,
    high_risk_count: highFindingCount + uncoveredProtectionCount,
    entities_monitored: entities.length,
    entities_under_protection_count: protectionEntities.length,
    themes_frequency: themesFrequency,
  };
};

export const buildOwnershipResultsSummary = (
  findings: IOwnershipFinding[],
  entities: IOwnershipMonitoredEntity[],
): IOwnershipResultsSummary => {
  const themesFrequency = findings.reduce<Record<string, number>>((frequencyMap, finding) => {
    const theme = finding.theme.trim().toLowerCase();

    if (!theme) {
      return frequencyMap;
    }

    return {
      ...frequencyMap,
      [theme]: (frequencyMap[theme] || 0) + 1,
    };
  }, {});

  return {
    total_findings: findings.length,
    high_risk_count: findings.filter(isHighSeverityOwnershipFinding).length,
    entities_monitored: entities.length,
    entities_with_active_contagion_count: entities.filter(isActiveOwnershipContagion).length,
    themes_frequency: themesFrequency,
  };
};

export const buildClimateResultsSummary = (
  findings: IClimateFinding[],
  entities: IClimateMonitoredEntity[],
): IClimateResultsSummary => {
  const themesFrequency = findings.reduce<Record<string, number>>((frequencyMap, finding) => {
    const theme = finding.theme.trim().toLowerCase();

    if (!theme) {
      return frequencyMap;
    }

    return {
      ...frequencyMap,
      [theme]: (frequencyMap[theme] || 0) + 1,
    };
  }, {});

  return {
    total_findings: findings.length,
    high_risk_count: findings.filter(isHighSeverityClimateFinding).length,
    entities_monitored: entities.length,
    entities_with_active_hazard_count: entities.filter(isActiveClimateHazard).length,
    themes_frequency: themesFrequency,
  };
};

export const filterClimateFindingsByTheme = (
  findings: IClimateFinding[],
  themeFilter: string = 'all',
): IClimateFinding[] => (
  themeFilter === 'all'
    ? findings
    : findings.filter((finding) => finding.theme === themeFilter)
);

export const getClimateAtRiskUnitIdsFromFindings = (
  findings: IClimateFinding[],
  monitoredUnitIds: number[],
  monitoredEntities: { unitId: number, name: string }[] = [],
): Set<number> => {
  const highRiskFindings = findings.filter(isHighSeverityClimateFinding);

  if (!highRiskFindings.length) {
    return new Set();
  }

  const monitoredSet = new Set(
    monitoredUnitIds.filter((unitId) => Number.isInteger(unitId) && unitId > 0),
  );

  if (!monitoredSet.size) {
    return new Set();
  }

  const monitoredIdByName = new Map(
    monitoredEntities
      .filter((entity) => monitoredSet.has(entity.unitId) && entity.name.trim())
      .map((entity) => [entity.name.trim().toLowerCase(), entity.unitId]),
  );

  const resolveMonitoredUnitId = (
    unitId: number,
    name?: string,
  ): number | null => {
    if (monitoredSet.has(unitId)) {
      return unitId;
    }

    if (!name?.trim()) {
      return null;
    }

    return monitoredIdByName.get(name.trim().toLowerCase()) ?? null;
  };

  const specificAtRiskIds = new Set<number>();

  highRiskFindings.forEach((finding) => {
    const resolvedUnitId = resolveMonitoredUnitId(
      finding.entity_unit_id,
      finding.entity_name,
    );

    if (resolvedUnitId != null) {
      specificAtRiskIds.add(resolvedUnitId);
    }
  });

  return specificAtRiskIds;
};

export const getAtRiskUnitIds = (
  results: TMonitoringAnySignalResult | null | undefined,
  monitoredUnitIds: number[],
  monitoredEntities: Array<{ unitId: number, name: string }> = [],
): Set<number> => {
  if (isRegulationMonitoringSignalResult(results)) {
    const impactedUnitIds = results.regulations_found
      .filter(isHighImpactRegulation)
      .map((regulation) => regulation.product_unit_id)
      .filter((unitId) => Number.isInteger(unitId) && unitId > 0);

    if (!impactedUnitIds.length || !monitoredUnitIds.length) {
      return new Set();
    }

    const monitoredSet = new Set(monitoredUnitIds);

    // Only units that resolve in the monitored/board set (CTA + badges require intersection).
    return new Set(impactedUnitIds.filter((unitId) => monitoredSet.has(unitId)));
  }

  if (isFinanceMonitoringSignalResult(results)) {
    if (!results.summary?.high_risk_count || results.summary.high_risk_count <= 0) {
      return new Set();
    }

    const monitoredSet = new Set(
      monitoredUnitIds.filter((unitId) => Number.isInteger(unitId) && unitId > 0),
    );

    if (!monitoredSet.size) {
      return new Set();
    }

    // Schedule-run fixtures may use demo unit IDs; map to live board IDs by supplier name.
    const monitoredIdByName = new Map(
      monitoredEntities
        .filter((entity) => monitoredSet.has(entity.unitId) && entity.name.trim())
        .map((entity) => [entity.name.trim().toLowerCase(), entity.unitId]),
    );

    const resolveMonitoredUnitId = (
      unitId: number,
      name?: string,
    ): number | null => {
      if (monitoredSet.has(unitId)) {
        return unitId;
      }

      if (!name?.trim()) {
        return null;
      }

      const mappedUnitId = monitoredIdByName.get(name.trim().toLowerCase());

      return mappedUnitId ?? null;
    };

    const specificAtRiskIds = new Set<number>();

    results.entities.forEach((entity) => {
      if (!isActiveFinanceBankruptcy(entity)) {
        return;
      }

      const resolvedUnitId = resolveMonitoredUnitId(entity.unitId, entity.name);

      if (resolvedUnitId != null) {
        specificAtRiskIds.add(resolvedUnitId);
      }
    });

    results.findings.forEach((finding) => {
      if (!isHighSeverityFinanceFinding(finding)) {
        return;
      }

      const resolvedUnitId = resolveMonitoredUnitId(
        finding.supplier_unit_id,
        finding.supplier_name,
      );

      if (resolvedUnitId != null) {
        specificAtRiskIds.add(resolvedUnitId);
      }
    });

    // Finance badges are entity-specific (unlike reputation, which flags all monitored).
    return specificAtRiskIds;
  }

  if (isOwnershipMonitoringSignalResult(results)) {
    if (!results.summary?.high_risk_count || results.summary.high_risk_count <= 0) {
      return new Set();
    }

    const monitoredSet = new Set(
      monitoredUnitIds.filter((unitId) => Number.isInteger(unitId) && unitId > 0),
    );

    if (!monitoredSet.size) {
      return new Set();
    }

    // Schedule-run fixtures may use demo unit IDs; map to live board IDs by supplier name.
    const monitoredIdByName = new Map(
      monitoredEntities
        .filter((entity) => monitoredSet.has(entity.unitId) && entity.name.trim())
        .map((entity) => [entity.name.trim().toLowerCase(), entity.unitId]),
    );

    const resolveMonitoredUnitId = (
      unitId: number,
      name?: string,
    ): number | null => {
      if (monitoredSet.has(unitId)) {
        return unitId;
      }

      if (!name?.trim()) {
        return null;
      }

      const mappedUnitId = monitoredIdByName.get(name.trim().toLowerCase());

      return mappedUnitId ?? null;
    };

    const specificAtRiskIds = new Set<number>();

    // Ownership badges are finding-driven (no bankruptcy-style entity flag to fall back on).
    results.findings.forEach((finding) => {
      if (!isHighSeverityOwnershipFinding(finding)) {
        return;
      }

      const resolvedUnitId = resolveMonitoredUnitId(
        finding.supplier_unit_id,
        finding.supplier_name,
      );

      if (resolvedUnitId != null) {
        specificAtRiskIds.add(resolvedUnitId);
      }
    });

    return specificAtRiskIds;
  }

  if (isClimateMonitoringSignalResult(results)) {
    if (!results.summary?.high_risk_count || results.summary.high_risk_count <= 0) {
      return new Set();
    }

    return getClimateAtRiskUnitIdsFromFindings(
      results.findings,
      monitoredUnitIds,
      monitoredEntities,
    );
  }

  if (!isReputationMonitoringSignalResult(results)
    || !results.summary?.high_risk_count
    || results.summary.high_risk_count <= 0) {
    return new Set();
  }

  return new Set(
    monitoredUnitIds.filter((unitId) => Number.isInteger(unitId) && unitId > 0),
  );
};

export const buildFallbackMonitoringResults = (
  entities: { unitId: number, name: string }[],
): IMonitoringSignalResult => {
  const articles: IMonitoringArticle[] = entities.flatMap((entity, entityIndex) => (
    FALLBACK_ARTICLE_TEMPLATES.slice(0, entityIndex === 0 ? 2 : 1).map((template, templateIndex) => {
      const primaryKeyword = entity.name.split(' ')[0];

      return {
        ...template,
        title: templateIndex === 0
          ? `${entity.name} linked to ${
            template.risk_severity === 'high' ? 'financial distress coverage' : 'regulatory mention'
          }`
          : `Public discussion references ${entity.name}`,
        keywords_found: [primaryKeyword, 'supplier', 'reputation', template.risk_severity],
        date: `2026-07-${String(15 - entityIndex - templateIndex).padStart(2, '0')}`,
      };
    })
  ));

  const sortedArticles = sortMonitoringArticles(articles);

  return {
    articles_found: sortedArticles,
    summary: buildResultsSummary(sortedArticles),
  };
};

export const buildFallbackRegulationResults = (
  entities: { unitId: number, name: string }[],
): IRegulationMonitoringSignalResult => {
  const regulations: IRegulationSignal[] = entities.flatMap((entity, entityIndex) => {
    const template = FALLBACK_REGULATION_TEMPLATES[
      entityIndex % FALLBACK_REGULATION_TEMPLATES.length
    ];

    return [{
      ...template,
      id: `reg-fallback-${entity.unitId}-${entityIndex}`,
      product_name: entity.name,
      product_unit_id: entity.unitId,
      impact_level: entityIndex === 0 ? 'high' : template.impact_level,
      approval_state: entityIndex === 0 ? 'in-force' : template.approval_state,
      source_type: entityIndex === 0 ? 'official' : template.source_type,
    }];
  });

  const sortedRegulations = sortRegulationSignals(regulations);

  return {
    regulations_found: sortedRegulations,
    summary: buildRegulationResultsSummary(sortedRegulations),
  };
};

const FINANCE_FALLBACK_PROFILES: Record<number, Omit<IFinanceMonitoredEntity, 'unitId' | 'name'>> = {
  47426: {
    stability_score: 29,
    trend: 'declining',
    spike: true,
    currency: 'USD',
    related_materials: ['Polyethylene Film', 'Adhesive Materials'],
    financial_profile: {
      years: [
        {
          year_index: 1, fiscal_year: 2025, revenue: 420000000, ebitda: 38000000, liquidity_retention: 0.41,
        },
        {
          year_index: 2, fiscal_year: 2024, revenue: 455000000, ebitda: 52000000, liquidity_retention: 0.48,
        },
        {
          year_index: 3, fiscal_year: 2023, revenue: 490000000, ebitda: 61000000, liquidity_retention: 0.55,
        },
        {
          year_index: 4, fiscal_year: 2022, revenue: 510000000, ebitda: 67000000, liquidity_retention: 0.58,
        },
        {
          year_index: 5, fiscal_year: 2021, revenue: 505000000, ebitda: 64000000, liquidity_retention: 0.57,
        },
      ],
    },
    bankruptcy_status: {
      status: 'none',
      label: 'No bankruptcy protection or judicial reorganization',
      is_active: false,
      history: [],
    },
    alerts: [
      {
        theme: 'Liquidity',
        title: 'Liquidity retention (buffer) 0.41x — below 1.0 for 3 years',
        severity: 'high',
        source: 'Annual report',
      },
      {
        theme: 'Earnings',
        title: 'Revenue & EBITDA multi-year decline',
        severity: 'high',
        source: 'Financial statements',
      },
    ],
  },
  47427: {
    stability_score: 68,
    trend: 'stable',
    spike: false,
    currency: 'USD',
    related_materials: ['Polyurethane Film', 'Silicone Gel'],
    financial_profile: {
      years: [
        {
          year_index: 1, fiscal_year: 2025, revenue: 6100000000, ebitda: 780000000, liquidity_retention: 1.12,
        },
        {
          year_index: 2, fiscal_year: 2024, revenue: 6050000000, ebitda: 760000000, liquidity_retention: 1.18,
        },
        {
          year_index: 3, fiscal_year: 2023, revenue: 5980000000, ebitda: 740000000, liquidity_retention: 1.21,
        },
        {
          year_index: 4, fiscal_year: 2022, revenue: 6200000000, ebitda: 810000000, liquidity_retention: 1.25,
        },
        {
          year_index: 5, fiscal_year: 2021, revenue: 5900000000, ebitda: 720000000, liquidity_retention: 1.15,
        },
      ],
    },
    bankruptcy_status: {
      status: 'none',
      label: 'No bankruptcy protection or judicial reorganization',
      is_active: false,
      history: [],
    },
    alerts: [
      {
        theme: 'Credit',
        title: 'Credit rating outlook revised to negative',
        severity: 'medium',
        source: 'S&P bulletin',
      },
    ],
  },
  47428: {
    stability_score: 12,
    trend: 'declining',
    spike: true,
    currency: 'USD',
    related_materials: ['Silicone Gel', 'Non-Woven Adhesive Material'],
    financial_profile: {
      years: [
        {
          year_index: 1, fiscal_year: 2025, revenue: 180000000, ebitda: -12000000, liquidity_retention: 0.18,
        },
        {
          year_index: 2, fiscal_year: 2024, revenue: 210000000, ebitda: 8000000, liquidity_retention: 0.29,
        },
        {
          year_index: 3, fiscal_year: 2023, revenue: 240000000, ebitda: 22000000, liquidity_retention: 0.36,
        },
        {
          year_index: 4, fiscal_year: 2022, revenue: 250000000, ebitda: 28000000, liquidity_retention: 0.40,
        },
        {
          year_index: 5, fiscal_year: 2021, revenue: 245000000, ebitda: 30000000, liquidity_retention: 0.42,
        },
      ],
    },
    bankruptcy_status: {
      status: 'chapter-11',
      label: 'Operating under Chapter 11 bankruptcy protection',
      jurisdiction: 'United States',
      case_name: 'In re Wacker Chemie AG (demo)',
      filed_at: '2026-03-12',
      is_active: true,
      history: [
        {
          status: 'chapter-11',
          filed_at: '2026-03-12',
          exited_at: null,
          note: 'Voluntary petition filed; debtor-in-possession operations continuing',
        },
      ],
    },
    alerts: [
      {
        theme: 'Insolvency',
        title: 'Chapter 11 — operating under bankruptcy protection',
        severity: 'critical',
        source: 'US Bankruptcy Court',
      },
      {
        theme: 'Liquidity',
        title: 'Liquidity retention (buffer) 0.18x',
        severity: 'critical',
        source: 'Annual report',
      },
    ],
  },
  47420: {
    stability_score: 82,
    trend: 'stable',
    spike: false,
    currency: 'USD',
    related_materials: ['Sterile Absorbent Dressing Pads'],
    financial_profile: {
      years: [
        {
          year_index: 1, fiscal_year: 2025, revenue: 8200000000, ebitda: 1900000000, liquidity_retention: 1.45,
        },
        {
          year_index: 2, fiscal_year: 2024, revenue: 8000000000, ebitda: 1850000000, liquidity_retention: 1.42,
        },
        {
          year_index: 3, fiscal_year: 2023, revenue: 7900000000, ebitda: 1800000000, liquidity_retention: 1.40,
        },
        {
          year_index: 4, fiscal_year: 2022, revenue: 7700000000, ebitda: 1750000000, liquidity_retention: 1.38,
        },
        {
          year_index: 5, fiscal_year: 2021, revenue: 7500000000, ebitda: 1700000000, liquidity_retention: 1.35,
        },
      ],
    },
    bankruptcy_status: {
      status: 'none',
      label: 'No bankruptcy protection or judicial reorganization',
      is_active: false,
      history: [],
    },
    alerts: [
      {
        theme: 'Market',
        title: 'No material financial distress signals in scan window',
        severity: 'low',
        source: 'Financial analytics',
      },
    ],
  },
};

const FINANCE_FALLBACK_FINDINGS: Record<number, IFinanceFinding[]> = {
  47426: [
    {
      id: 'fin-fallback-47426-1',
      supplier_name: 'Polymer Technologies',
      supplier_unit_id: 47426,
      related_materials: ['Polyethylene Film', 'Adhesive Materials'],
      theme: 'liquidity',
      title: 'Liquidity retention (buffer) below 1.0 for three consecutive years',
      severity: 'high',
      confidence: 0.86,
      observed_at: '2026-07-14',
      source_type: 'filing',
      source_label: 'Annual report / financial analytics',
      source_url: 'https://example.com/filings/polymer-fy2025',
      metric: {
        name: 'liquidity_retention',
        formula: 'liquid_assets / current_liabilities',
        value: 0.41,
        unit: 'ratio',
        prior_value: 0.58,
        trend: 'declining',
        year: 1,
        components: { liquid_assets: 82000000, current_liabilities: 200000000 },
      },
      shortage_risk: {
        level: 'elevated',
        summary: 'Liquidity buffer below 1.0x raises risk of delayed polymer feedstock into Tier-1 Input Products.',
      },
      recommended_action:
        'Confirm open POs and dual-source readiness for Polyethylene Film; increase monitoring cadence.',
    },
    {
      id: 'fin-fallback-47426-2',
      supplier_name: 'Polymer Technologies',
      supplier_unit_id: 47426,
      related_materials: ['Polyethylene Film'],
      theme: 'earnings',
      title: 'Revenue and EBITDA declined across the past five fiscal years',
      severity: 'high',
      confidence: 0.81,
      observed_at: '2026-07-14',
      source_type: 'filing',
      source_label: 'Multi-year financial statements',
      source_url: 'https://example.com/filings/polymer-trend',
      shortage_risk: {
        level: 'elevated',
        summary:
          'Shrinking earnings franchise increases the chance of capacity cuts affecting film and adhesive inputs.',
      },
      recommended_action: 'Map sole-source SKUs fed by Polymer Technologies and review safety stock.',
    },
  ],
  47427: [
    {
      id: 'fin-fallback-47427-1',
      supplier_name: 'Huntsman Corporation',
      supplier_unit_id: 47427,
      related_materials: ['Polyurethane Film', 'Silicone Gel'],
      theme: 'credit',
      title: 'Credit rating outlook revised to negative',
      severity: 'medium',
      confidence: 0.78,
      observed_at: '2026-07-13',
      source_type: 'agency',
      source_label: 'S&P bulletin',
      source_url: 'https://example.com/ratings/huntsman-outlook',
      shortage_risk: {
        level: 'watch',
        summary: 'Higher borrowing costs may pressure specialty chemical allocation to non-priority customers.',
      },
      recommended_action: 'Watch allocation notices; keep alternate specialty suppliers on shortlist.',
    },
  ],
  47428: [
    {
      id: 'fin-fallback-47428-1',
      supplier_name: 'Wacker Chemie AG',
      supplier_unit_id: 47428,
      related_materials: ['Silicone Gel', 'Non-Woven Adhesive Material'],
      theme: 'insolvency',
      title: 'Operating under Chapter 11 bankruptcy protection',
      severity: 'critical',
      confidence: 0.94,
      observed_at: '2026-03-12',
      source_type: 'court',
      source_label: 'US Bankruptcy Court filing',
      source_url: 'https://example.com/court/wacker-ch11',
      shortage_risk: {
        level: 'critical',
        summary:
          'Court-supervised operations raise immediate allocation / stop-supply risk '
          + 'for silicone and adhesive materials.',
      },
      recommended_action: 'Launch Impact Scenario for affected Input Products; qualify alternate sources immediately.',
    },
  ],
};

export const buildFallbackFinanceResults = (
  entities: { unitId: number, name: string }[],
): IFinanceMonitoringSignalResult => {
  const financeEntities: IFinanceMonitoredEntity[] = entities.map((entity, entityIndex) => {
    const profile = FINANCE_FALLBACK_PROFILES[entity.unitId];

    if (profile) {
      return {
        unitId: entity.unitId,
        name: entity.name,
        ...profile,
      };
    }

    const liquidity = entityIndex === 0 ? 0.55 : 1.1;
    const isDistressed = entityIndex === 0;

    return {
      unitId: entity.unitId,
      name: entity.name,
      stability_score: isDistressed ? 35 : 75,
      trend: isDistressed ? 'declining' : 'stable',
      spike: isDistressed,
      currency: 'USD',
      financial_profile: {
        years: [1, 2, 3, 4, 5].map((yearIndex) => ({
          year_index: yearIndex,
          fiscal_year: 2026 - yearIndex,
          revenue: isDistressed ? 400000000 - yearIndex * 10000000 : 900000000,
          ebitda: isDistressed ? 40000000 - yearIndex * 5000000 : 120000000,
          liquidity_retention: isDistressed ? liquidity - yearIndex * 0.02 : 1.2,
        })),
      },
      bankruptcy_status: {
        status: 'none',
        label: 'No bankruptcy protection or judicial reorganization',
        is_active: false,
        history: [],
      },
      alerts: isDistressed
        ? [{
          theme: 'Liquidity',
          title: 'Liquidity retention below 1.0x',
          severity: 'high' as const,
          source: 'Financial analytics',
        }]
        : [],
    };
  });

  const findings: IFinanceFinding[] = entities.flatMap((entity) => {
    const knownFindings = FINANCE_FALLBACK_FINDINGS[entity.unitId];

    if (knownFindings) {
      return knownFindings.map((finding) => ({
        ...finding,
        supplier_name: entity.name,
        supplier_unit_id: entity.unitId,
      }));
    }

    // Known healthy profiles (e.g. 47420) intentionally have no high-severity findings.
    if (FINANCE_FALLBACK_PROFILES[entity.unitId]) {
      return [];
    }

    if (entity.unitId === entities[0]?.unitId) {
      return [{
        id: `fin-fallback-${entity.unitId}-generic`,
        supplier_name: entity.name,
        supplier_unit_id: entity.unitId,
        theme: 'liquidity',
        title: `Liquidity pressure signals detected for ${entity.name}`,
        severity: 'high' as const,
        confidence: 0.8,
        observed_at: '2026-07-14',
        source_label: 'Financial analytics',
        shortage_risk: {
          level: 'elevated',
          summary: `Elevated shortage risk from financial stress at ${entity.name}.`,
        },
        recommended_action: 'Review open POs and dual-source readiness.',
      }];
    }

    return [];
  });

  const sortedFindings = sortFinanceFindings(findings);

  return {
    findings: sortedFindings,
    entities: financeEntities,
    summary: buildFinanceResultsSummary(sortedFindings, financeEntities),
  };
};

const OWNERSHIP_FALLBACK_PROFILES: Record<number, Omit<IOwnershipMonitoredEntity, 'unitId' | 'name'>> = {
  47426: {
    contagion_score: 82,
    trend: 'rising',
    spike: true,
    related_materials: ['Polyethylene Film', 'Adhesive Materials'],
    ownership_profile: {
      ultimate_beneficial_owner: { name: 'Chen Family Trust', type: 'person-or-trust', jurisdiction: 'Singapore' },
      direct_parent: {
        name: 'NorthStar Industrial Holdings', type: 'company', ownership_stake_pct: 87, jurisdiction: 'Germany',
      },
      corporate_group: 'NorthStar Industrial Group',
      related_entities_scanned: 14,
      related_entities_flagged: 4,
      group_entities_preview: [
        {
          name: 'NorthStar Apparel Manufacturing', role: 'Sister subsidiary', jurisdiction: 'Vietnam', risk_flag: true,
        },
        {
          name: 'NorthStar Specialty Chemicals GmbH',
          role: 'Sister subsidiary',
          jurisdiction: 'Germany',
          risk_flag: true,
        },
        {
          name: 'Chen Family Foundation', role: 'UBO foundation', jurisdiction: 'Singapore', risk_flag: true,
        },
      ],
    },
    investigation_tree: [
      {
        id: 'poly-parent',
        name: 'NorthStar Industrial Holdings',
        role: 'Direct parent',
        jurisdiction: 'Germany',
        ownership_stake_pct: 87,
        risk_flag: true,
        children: [
          {
            id: 'poly-sister-apparel',
            name: 'NorthStar Apparel Manufacturing',
            role: 'Sister subsidiary',
            jurisdiction: 'Vietnam',
            risk_flag: true,
            finding_ids: ['own-fallback-47426-1'],
          },
          {
            id: 'poly-sister-chem',
            name: 'NorthStar Specialty Chemicals GmbH',
            role: 'Sister subsidiary',
            jurisdiction: 'Germany',
            risk_flag: true,
            children: [
              {
                id: 'poly-plant-a',
                name: 'NorthStar Specialty Chemicals — Plant A',
                role: 'Operating site',
                jurisdiction: 'Germany',
                risk_flag: true,
                finding_ids: ['own-fallback-47426-2'],
              },
            ],
          },
        ],
      },
      {
        id: 'poly-ubo',
        name: 'Chen Family Trust',
        role: 'Ultimate beneficial owner',
        jurisdiction: 'Singapore',
        risk_flag: true,
        children: [
          {
            id: 'poly-foundation',
            name: 'Chen Family Foundation',
            role: 'UBO foundation',
            jurisdiction: 'Singapore',
            risk_flag: true,
            finding_ids: ['own-fallback-47426-3'],
          },
        ],
      },
    ],
    alerts: [
      {
        theme: 'Labour',
        title: 'Child labour investigation at NorthStar Apparel Manufacturing (sister subsidiary)',
        severity: 'critical',
        source: 'NGO field investigation',
      },
      {
        theme: 'Environmental',
        title: 'Sister chemical plant under regulator review',
        severity: 'high',
        source: 'EU environmental gazette',
      },
    ],
  },
  47428: {
    contagion_score: 61,
    trend: 'rising',
    spike: true,
    related_materials: ['Silicone Gel'],
    ownership_profile: {
      ultimate_beneficial_owner: { name: 'Alpine Capital Partners', type: 'company', jurisdiction: 'Switzerland' },
      direct_parent: {
        name: 'Alpine Capital Partners', type: 'company', ownership_stake_pct: 51, jurisdiction: 'Switzerland',
      },
      corporate_group: 'Alpine Capital portfolio',
      related_entities_scanned: 22,
      related_entities_flagged: 3,
      group_entities_preview: [
        {
          name: 'Alpine Petrochemicals SpA', role: 'Portfolio company', jurisdiction: 'Italy', risk_flag: true,
        },
        {
          name: 'Alpine Trade Desk LLC', role: 'Trading affiliate', jurisdiction: 'United States', risk_flag: true,
        },
      ],
    },
    investigation_tree: [
      {
        id: 'wacker-ubo',
        name: 'Alpine Capital Partners',
        role: 'Ultimate beneficial owner / Direct parent',
        jurisdiction: 'Switzerland',
        ownership_stake_pct: 51,
        risk_flag: true,
        finding_ids: ['own-fallback-47428-1'],
        children: [
          {
            id: 'wacker-petro',
            name: 'Alpine Petrochemicals SpA',
            role: 'Portfolio company',
            jurisdiction: 'Italy',
            risk_flag: true,
            children: [
              {
                id: 'wacker-trade',
                name: 'Alpine Trade Desk LLC',
                role: 'Trading affiliate',
                jurisdiction: 'United States',
                risk_flag: true,
                finding_ids: ['own-fallback-47428-2'],
              },
            ],
          },
        ],
      },
    ],
    alerts: [
      {
        theme: 'Reputation',
        title: 'UBO named in cross-border corruption probe',
        severity: 'high',
        source: 'International business press',
      },
      {
        theme: 'Sanctions',
        title: 'Portfolio company on enhanced due-diligence watchlist',
        severity: 'high',
        source: 'Trade compliance screening',
      },
    ],
  },
  47427: {
    contagion_score: 18,
    trend: 'stable',
    spike: false,
    related_materials: ['Polyurethane Film'],
    ownership_profile: {
      ultimate_beneficial_owner: { name: 'Huntsman Family', type: 'person-or-trust', jurisdiction: 'United States' },
      direct_parent: {
        name: 'Huntsman Holdings LLC', type: 'company', ownership_stake_pct: 100, jurisdiction: 'United States',
      },
      corporate_group: 'Huntsman Corporation',
      related_entities_scanned: 31,
      related_entities_flagged: 0,
      group_entities_preview: [],
    },
    investigation_tree: [
      {
        id: 'huntsman-parent',
        name: 'Huntsman Holdings LLC',
        role: 'Direct parent',
        jurisdiction: 'United States',
        ownership_stake_pct: 100,
        risk_flag: false,
        finding_ids: [],
        children: [
          {
            id: 'huntsman-ubo',
            name: 'Huntsman Family',
            role: 'Ultimate beneficial owner',
            jurisdiction: 'United States',
            risk_flag: false,
            finding_ids: [],
          },
        ],
      },
    ],
    alerts: [],
  },
};

const OWNERSHIP_FALLBACK_FINDINGS: Record<number, IOwnershipFinding[]> = {
  47426: [
    {
      id: 'own-fallback-47426-1',
      supplier_name: 'Polymer Technologies',
      supplier_unit_id: 47426,
      related_materials: ['Polyethylene Film', 'Adhesive Materials'],
      theme: 'labour-rights',
      title: 'Child labour investigation at sister subsidiary linked to parent of Polymer Technologies',
      severity: 'critical',
      confidence: 0.91,
      observed_at: '2026-07-18',
      source_type: 'ngo-report',
      source_label: 'NGO field investigation',
      source_url: 'https://example.com/reports/northstar-labour-2026',
      link_path: [
        { entity_name: 'Polymer Technologies', role: 'Monitored supplier' },
        { entity_name: 'NorthStar Industrial Holdings', role: 'Direct parent' },
        { entity_name: 'NorthStar Apparel Manufacturing', role: 'Sister subsidiary' },
      ],
      contagion_mechanism:
        'OEM customers auditing entire NorthStar group after labour findings at sister subsidiary',
      shortage_risk: {
        level: 'critical',
        summary:
          'Group-wide stop-ship and audit requests may deprioritize polymer allocations to external customers '
          + 'including your film inputs.',
      },
      recommended_action:
        'Escalate to supplier management; request independent audit certificate; prepare dual-source '
        + 'for Polyethylene Film.',
    },
    {
      id: 'own-fallback-47426-2',
      supplier_name: 'Polymer Technologies',
      supplier_unit_id: 47426,
      related_materials: ['Adhesive Materials'],
      theme: 'environmental',
      title: 'NorthStar Specialty Chemicals plant under regulator review after wastewater breach',
      severity: 'high',
      confidence: 0.82,
      observed_at: '2026-07-12',
      source_type: 'regulator',
      source_label: 'EU environmental gazette',
      source_url: 'https://example.com/regulator/northstar-wastewater',
      link_path: [
        { entity_name: 'Polymer Technologies', role: 'Monitored supplier' },
        { entity_name: 'NorthStar Industrial Holdings', role: 'Direct parent' },
        { entity_name: 'NorthStar Specialty Chemicals GmbH', role: 'Sister subsidiary' },
        { entity_name: 'NorthStar Specialty Chemicals — Plant A', role: 'Operating site' },
      ],
      contagion_mechanism:
        'Shared group permits and chemical park utilities mean a sister-plant review can constrain feedstock '
        + 'routing to Polymer Technologies',
      shortage_risk: {
        level: 'elevated',
        summary: 'Permit remediation at sister chemical plant may divert intermediate chemicals away from '
          + 'Polymer Technologies lines.',
      },
      recommended_action: 'Map shared intermediates; ask Polymer Technologies for contingency allocation plan.',
    },
    {
      id: 'own-fallback-47426-3',
      supplier_name: 'Polymer Technologies',
      supplier_unit_id: 47426,
      related_materials: ['Polyethylene Film'],
      theme: 'reputation-contagion',
      title: 'Chen Family Foundation named in bribery allegations in Southeast Asia media',
      severity: 'high',
      confidence: 0.74,
      observed_at: '2026-07-15',
      source_type: 'news',
      source_label: 'Regional business press',
      source_url: 'https://example.com/news/chen-foundation',
      link_path: [
        { entity_name: 'Polymer Technologies', role: 'Monitored supplier' },
        { entity_name: 'Chen Family Trust', role: 'Ultimate beneficial owner' },
        { entity_name: 'Chen Family Foundation', role: 'UBO foundation' },
      ],
      contagion_mechanism:
        'Reputational fallout from UBO-linked entity may trigger customer procurement reviews across group',
      shortage_risk: {
        level: 'elevated',
        summary: 'OEM procurement reviews of NorthStar group could slow PO renewals for polymer feedstock.',
      },
      recommended_action: 'Monitor sanctions watchlist updates for Chen Family Trust; confirm current PO coverage.',
    },
  ],
  47428: [
    {
      id: 'own-fallback-47428-1',
      supplier_name: 'Wacker Chemie AG',
      supplier_unit_id: 47428,
      related_materials: ['Silicone Gel'],
      theme: 'reputation-contagion',
      title: 'Ultimate beneficial owner named in cross-border corruption probe',
      severity: 'high',
      confidence: 0.78,
      observed_at: '2026-07-16',
      source_type: 'news',
      source_label: 'International business press',
      source_url: 'https://example.com/news/alpine-corruption',
      link_path: [
        { entity_name: 'Wacker Chemie AG', role: 'Monitored supplier' },
        { entity_name: 'Alpine Capital Partners', role: 'Ultimate beneficial owner' },
      ],
      contagion_mechanism: 'Banking and insurance review across Alpine portfolio companies',
      shortage_risk: {
        level: 'elevated',
        summary:
          'Payment and credit friction at group level can delay silicone feedstock shipments even if operating '
          + 'subsidiary remains open.',
      },
      recommended_action: 'Confirm payment terms and open PO coverage; monitor for sanctions watchlist updates.',
    },
    {
      id: 'own-fallback-47428-2',
      supplier_name: 'Wacker Chemie AG',
      supplier_unit_id: 47428,
      related_materials: ['Silicone Gel'],
      theme: 'sanctions',
      title: 'Alpine Petrochemicals SpA added to enhanced due-diligence watchlist after trade routing alert',
      severity: 'high',
      confidence: 0.86,
      observed_at: '2026-07-17',
      source_type: 'watchlist',
      source_label: 'Trade compliance screening',
      source_url: 'https://example.com/watchlist/alpine-petro',
      link_path: [
        { entity_name: 'Wacker Chemie AG', role: 'Monitored supplier' },
        { entity_name: 'Alpine Capital Partners', role: 'Ultimate beneficial owner' },
        { entity_name: 'Alpine Petrochemicals SpA', role: 'Portfolio company' },
        { entity_name: 'Alpine Trade Desk LLC', role: 'Trading affiliate' },
      ],
      contagion_mechanism:
        'Shared treasury and trade-finance facilities across Alpine portfolio can freeze outbound shipments '
        + 'from Wacker Chemie AG',
      shortage_risk: {
        level: 'critical',
        summary: 'Trade-finance holds on Alpine affiliates may interrupt silicone gel deliveries into your '
          + 'Own Operations.',
      },
      recommended_action: 'Escalate compliance check; confirm alternate silicone sources for critical SKUs.',
    },
  ],
};

export const buildFallbackOwnershipResults = (
  entities: { unitId: number, name: string }[],
): IOwnershipMonitoringSignalResult => {
  const ownershipEntities: IOwnershipMonitoredEntity[] = entities.map((entity, entityIndex) => {
    const profile = OWNERSHIP_FALLBACK_PROFILES[entity.unitId];

    if (profile) {
      return {
        unitId: entity.unitId,
        name: entity.name,
        ...profile,
      };
    }

    const isExposed = entityIndex === 0;

    return {
      unitId: entity.unitId,
      name: entity.name,
      contagion_score: isExposed ? 58 : 15,
      trend: isExposed ? 'rising' : 'stable',
      spike: isExposed,
      ownership_profile: {
        ultimate_beneficial_owner: { name: 'Unknown / not disclosed', type: 'person-or-trust' },
        direct_parent: { name: entity.name, type: 'company' },
        related_entities_scanned: isExposed ? 6 : 3,
        related_entities_flagged: isExposed ? 1 : 0,
        group_entities_preview: [],
      },
      investigation_tree: [],
      alerts: isExposed
        ? [{
          theme: 'Governance',
          title: 'Ownership structure could not be fully verified',
          severity: 'medium' as const,
          source: 'Ownership analytics',
        }]
        : [],
    };
  });

  const findings: IOwnershipFinding[] = entities.flatMap((entity) => {
    const knownFindings = OWNERSHIP_FALLBACK_FINDINGS[entity.unitId];

    if (knownFindings) {
      return knownFindings.map((finding) => ({
        ...finding,
        supplier_name: entity.name,
        supplier_unit_id: entity.unitId,
      }));
    }

    // Known low-exposure profiles (e.g. Huntsman 47427) intentionally have no high-severity findings.
    if (OWNERSHIP_FALLBACK_PROFILES[entity.unitId]) {
      return [];
    }

    if (entity.unitId === entities[0]?.unitId) {
      return [{
        id: `own-fallback-${entity.unitId}-generic`,
        supplier_name: entity.name,
        supplier_unit_id: entity.unitId,
        theme: 'governance',
        title: `Ownership structure could not be fully verified for ${entity.name}`,
        severity: 'medium' as const,
        confidence: 0.6,
        observed_at: '2026-07-14',
        source_label: 'Ownership analytics',
        link_path: [
          { entity_name: entity.name, role: 'Monitored supplier' },
        ],
        shortage_risk: {
          level: 'watch',
          summary: `Incomplete ownership disclosure raises monitoring priority for ${entity.name}.`,
        },
        recommended_action: 'Request updated ownership attestation and beneficial-owner disclosure.',
      }];
    }

    return [];
  });

  const sortedFindings = sortOwnershipFindings(findings);

  return {
    findings: sortedFindings,
    entities: ownershipEntities,
    summary: buildOwnershipResultsSummary(sortedFindings, ownershipEntities),
  };
};

export const buildFallbackClimateResults = (
  entities: { unitId: number, name: string, entity_kind?: string }[],
): IClimateMonitoringSignalResult => {
  const climateEntities: IClimateMonitoredEntity[] = entities.map((entity, entityIndex) => {
    const exposureScore = Math.min(95, 48 + entityIndex * 12);

    return {
      unitId: entity.unitId,
      entity_kind: entity.entity_kind || 'supplier',
      name: entity.name,
      exposure_score: exposureScore,
      trend: 'rising',
      spike: exposureScore >= 60,
      location: { country: 'Unknown', region: null, locality: null },
      related_materials: [],
      climate_profile: {
        hazards_active: ['other-climate'],
        dominant_horizon: '7d',
        water_stress_index: null,
        notes: 'Fallback climate profile until live signals are available.',
      },
      finding_ids: [`cli-fallback-${entity.unitId}`],
      alerts: [
        {
          theme: 'Other',
          title: 'Climate exposure could not be fully verified',
          severity: exposureScore >= 70 ? 'high' : 'medium',
          source: 'Climate analytics',
        },
      ],
    };
  });

  const findings: IClimateFinding[] = entities.map((entity, entityIndex) => {
    const severity: TMonitoringRiskSeverity = entityIndex === 0 ? 'high' : 'medium';

    return {
      id: `cli-fallback-${entity.unitId}`,
      entity_kind: entity.entity_kind || 'supplier',
      entity_name: entity.name,
      entity_unit_id: entity.unitId,
      related_materials: [],
      location: { country: 'Unknown', region: null, locality: null },
      theme: 'other-climate',
      title: `Climate exposure could not be fully verified for ${entity.name}`,
      severity,
      confidence: 0.55,
      observed_at: new Date().toISOString().slice(0, 10),
      horizon: '7d',
      source_type: 'analytics',
      source_label: 'Climate analytics',
      shortage_risk: {
        level: 'elevated',
        summary: `Incomplete climate signal coverage raises monitoring priority for ${entity.name}.`,
      },
      recommended_action: 'Confirm site coordinates and linked Input Products for the next scan.',
      impact_trail: [
        { role: 'Hazard', entity_name: 'Unverified climate advisory' },
        { role: 'Entity', entity_name: entity.name },
        { role: 'Impact', entity_name: 'Monitoring priority elevated' },
      ],
    };
  });

  const sortedFindings = sortClimateFindings(findings);

  return {
    findings: sortedFindings,
    entities: climateEntities,
    summary: buildClimateResultsSummary(sortedFindings, climateEntities),
  };
};
